import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import './ModalTextEditorStyle.css'
import {
  ActionBeginEventArgs,
  Count,
  EmojiPicker,
  HtmlEditor,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-react-richtexteditor'
import { CHAT_LEFT_BUTTONS } from '@constants/Message'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import { VariableList } from '@components/pages/Messages/MockDataMessages'
import { generateSvg } from '@utils/svg.util'
import { useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'
import { Button } from '@components/common'

export interface IModalTextEditorProps {
  className?: string
  editorId?: string
  inputText: string
  inputHtml?: string
  maxCharCount?: number
  readOnly?: boolean
  resize?: boolean
  setInputText: Dispatch<SetStateAction<string>>
  setInputHtml?: Dispatch<SetStateAction<string>>
  showAiSuggestionButtons?: boolean
  showLink?: boolean
  showMention?: boolean
  showVariable?: boolean
  forceUpdate?: boolean
  variableList?: { text: string; value: string }[]
}

const ModalTextEditor: React.FC<IModalTextEditorProps> = ({
  className,
  editorId = 'target-input',
  inputText,
  inputHtml,
  maxCharCount,
  readOnly = false,
  resize = true,
  setInputText,
  setInputHtml,
  showAiSuggestionButtons = false,
  showLink = true,
  showMention = true,
  showVariable = true,
  forceUpdate = false,
  variableList,
}) => {
  const patients = useAppSelector((state) => state.patient.data)

  const [isAiEnabled, setIsAiEnabled] = useState(false)
  const toolbarRef = useRef<RichTextEditorComponent>(null)
  const mentionRef = useRef<MentionComponent>(null)
  const variableRef = useRef<MentionComponent>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasUserEditedRef = useRef<boolean>(false)
  const lastEmittedHtmlRef = useRef<string | null>(null)

  const peopleList = useMemo(() => {
    return patients.map((patient) => ({ name: getFullName(patient) }))
  }, [patients])

  const handleToolbarActions = (buttonName: string): void => {
    const ref = toolbarRef.current

    switch (buttonName) {
      case 'variable':
        {
          if (variableRef.current && ref) {
            if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
            variableRef.current.showPopup()
          }
        }
        break
      case 'mention':
        if (mentionRef.current && ref) {
          if (ref.getHtml() === '<p><br></p>') ref.value = '<p>&nbsp;</p>'
          mentionRef.current.showPopup()
        }
        break
      case 'ai':
        {
          setIsAiEnabled((prevState) => !prevState)
        }
        break
      default:
        break
    }
  }

  const filteredButtons = useMemo(() => {
    return CHAT_LEFT_BUTTONS.filter((button) => {
      switch (button.key) {
        case 'url':
          return showLink
        case 'mention':
          return showMention
        case 'variable':
          return showVariable
        default:
          return true
      }
    })
  }, [showLink, showMention, showVariable])

  const htmlToPlainText = (html: string): string => {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li[^>]*>/gi, '- ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\n{3,}/g, '\n\n')
      .trimEnd()
  }

  const plainTextToHtml = (text: string): string => {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    return escaped
      .split(/\r?\n/)
      .map((line) => (line.length === 0 ? '<p><br></p>' : `<p>${line}</p>`))
      .join('')
  }

  const toolbarSettings: ToolbarSettingsModel = {
    items: filteredButtons.map((data) => {
      if (data.type === 'builtin') {
        return data.icon
      } else {
        return {
          template: generateSvg(
            data.key,
            data.icon,
            data.type === 'ti' ? 24 : 16,
          ),
          undo: true,
          tooltipText: data.text,
          click: () => {
            handleToolbarActions(data.key)
          },
        }
      }
    }),
    enable: true,
  }

  const injectedServices = useMemo(
    () => [
      Toolbar,
      Link,
      HtmlEditor,
      QuickToolbar,
      EmojiPicker,
      ...(maxCharCount ? [Count] : []),
    ],
    [],
  )

  const handleSend = (): void => {}

  const handleAIFeaturesButtons = (command: string): void => {
    let newInputPrompt = ''
    switch (command.toLowerCase()) {
      case 'reply':
        newInputPrompt = 'Generate a response for the following message:\n '
        break
      case 'summarize':
        newInputPrompt = `Summarize the message below. Make 
          sure to retain the same context with shorter number of characters.\n `
        break
    }

    if (toolbarRef.current) toolbarRef.current.value = newInputPrompt
    setInputText(newInputPrompt)
  }

  const onActionBegin = (e: ActionBeginEventArgs): void => {
    if (e.requestType === 'EnterAction') {
      e.cancel = false
    }
  }

  const onTextChange = useCallback((): void => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Set a new timeout to debounce the text change
    debounceTimeoutRef.current = setTimeout(() => {
      if (toolbarRef.current) {
        hasUserEditedRef.current = true
        const html = toolbarRef.current.getHtml()
        lastEmittedHtmlRef.current = html
        let text = htmlToPlainText(html)

        if (!inputHtml) {
          VariableList.forEach((variable) => {
            const escapedText = variable.text.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&',
            )
            const regex = new RegExp(escapedText, 'g')
            text = text.replace(regex, variable.value)
          })
        }
        setInputHtml?.(html)

        setInputText(text)
      }
    }, 100) // 100ms debounce
  }, [setInputText, setInputHtml, inputHtml])

  const onActionComplete = (): void => {
    // Trigger text change after any toolbar action completes
    onTextChange()
  }

  const onFocusOut = (): void => {
    // Trigger text change when focus is lost
    onTextChange()
  }

  const resolveEditorHtml = (
    html: string | undefined,
    text: string,
  ): string => {
    const source = html != null && html !== '' ? html : text
    if (!source) return ''
    return /<[^>]+>/.test(source) ? source : plainTextToHtml(source)
  }

  const onCreated = (): void => {
    if (toolbarRef.current) {
      const editorElement =
        toolbarRef.current.element?.querySelector('.e-content')
      if (editorElement) {
        editorElement.addEventListener('input', onTextChange)
        editorElement.addEventListener('paste', () =>
          setTimeout(onTextChange, 0),
        )
        editorElement.addEventListener('drop', () =>
          setTimeout(onTextChange, 0),
        )
      }

      // Uncontrolled after create — set via ref so typing does not fight React `value`.
      const initialHtml = resolveEditorHtml(inputHtml, inputText)
      toolbarRef.current.value = initialHtml
      lastEmittedHtmlRef.current = initialHtml
      hasUserEditedRef.current = false
    }
  }

  // Cleanup event listeners on unmount
  useEffect(() => {
    const editorInstance = toolbarRef.current
    return () => {
      // Clear any pending timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      if (editorInstance) {
        const editorElement =
          editorInstance.element?.querySelector('.e-content')
        if (editorElement) {
          editorElement.removeEventListener('input', onTextChange)
          editorElement.removeEventListener('paste', () =>
            setTimeout(onTextChange, 0),
          )
          editorElement.removeEventListener('drop', () =>
            setTimeout(onTextChange, 0),
          )
        }
      }
    }
  }, [onTextChange])

  // Sync from props for external updates (templates, channel reset). Skip echoes of our own edits.
  useEffect(() => {
    if (!toolbarRef.current) return

    const currentHtml = toolbarRef.current.getHtml()
    const isEditorEmpty =
      currentHtml === '<p><br></p>' || currentHtml === '' || currentHtml == null
    const nextHtml = resolveEditorHtml(inputHtml, inputText)
    const isEchoOfOwnEdit =
      lastEmittedHtmlRef.current != null &&
      nextHtml === lastEmittedHtmlRef.current
    const shouldApplyExternal =
      forceUpdate ||
      !hasUserEditedRef.current ||
      (!isEchoOfOwnEdit && nextHtml !== currentHtml)

    if (shouldApplyExternal && (isEditorEmpty || currentHtml !== nextHtml)) {
      toolbarRef.current.value = nextHtml
      lastEmittedHtmlRef.current = nextHtml
      if (forceUpdate || !isEchoOfOwnEdit) {
        hasUserEditedRef.current = false
      }
    }
  }, [inputText, inputHtml, forceUpdate])

  return (
    <div className={`modal-text-editor-comp ${className}`}>
      <div className={`${isAiEnabled ? 'border' : undefined} h-100`}>
        <RichTextEditorComponent
          id={editorId}
          ref={toolbarRef}
          toolbarSettings={toolbarSettings}
          actionBegin={onActionBegin}
          actionComplete={onActionComplete}
          created={onCreated}
          enableTabKey={true}
          cssClass="message-rte-tooltip"
          className={isAiEnabled ? 'glow' : 'default'}
          placeholder={
            isAiEnabled ? 'What would you like AI to do?' : 'Type a message'
          }
          style={resize ? { resize: 'vertical' } : undefined}
          change={onTextChange}
          blur={onFocusOut}
          showCharCount={!!maxCharCount}
          maxLength={maxCharCount}
          readOnly={readOnly}
          enabled={!readOnly}
        >
          <Inject services={injectedServices} />
        </RichTextEditorComponent>

        {isAiEnabled && (
          <>
            <div className="d-flex chat-right-buttons">
              <Button
                data-bs-toggle="tooltip"
                title="Send"
                key="modal-send-btn"
                id="custom-send-button"
                className="button-icon"
                onClick={handleSend}
              >
                <i className="mgc_send_fill"></i>
              </Button>
            </div>

            {showAiSuggestionButtons && (
              <div className="ai-toolbar-buttons-container pe-2">
                <button
                  data-bs-toggle="tooltip"
                  title="reply"
                  className="border-0 ai-toolbar-buttons"
                  onClick={() => handleAIFeaturesButtons('reply')}
                >
                  Reply to this message
                </button>
                <button
                  data-bs-toggle="tooltip"
                  title="summarize"
                  className="border-0 ai-toolbar-buttons"
                  onClick={() => handleAIFeaturesButtons('summarize')}
                >
                  Summarize
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <div className="tec-mention-comp">
        <MentionComponent
          target={`#${editorId}`}
          ref={variableRef}
          dataSource={variableList ?? VariableList}
          showMentionChar={false}
          mentionChar="("
          cssClass="variable-component-text"
          fields={{ text: 'text', value: 'value' }}
          suffixText={'&nbsp;'}
        />
        <MentionComponent
          target={`#${editorId}`}
          ref={mentionRef}
          dataSource={peopleList}
          showMentionChar={true}
          mentionChar="@"
          cssClass="mention-component-text"
          fields={{ text: 'name', value: 'name' }}
          suffixText={'&nbsp;'}
        />
      </div>
    </div>
  )
}

export default ModalTextEditor
